import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { ShippingApiService } from 'src/app/builder-services/api/shipping.api.service'
import { Country, City, State } from 'country-state-city'
import { VendorService } from 'src/app/multivendor/vendor.service'
@Injectable({
  providedIn: 'root'
})
export class ShippingService {
  public countriesCache: any
  constructor(
    private builderService: BuilderService,
    private vendor: VendorService,
    private shippingApi: ShippingApiService,
  ) {}

  /**
   * Saves the full shipping config for the current project.
   * Clears old data first then re-inserts the full list.
   * @param countries Full list of countries with their governorates/cities/areas
   * @returns Observable that emits true on success
   */
  submitCountry = (countries: any[]): Observable<any> => {
    const project_id = Number(this.builderService.selectedProject)
    this.countriesCache = null
    return new Observable(observer => {
      this.saveShipping(project_id, countries)
        .then(() => { observer.next(true); observer.complete() })
        .catch(err => { console.warn('[ShippingService] save error:', err); observer.error(err) })
    })
  }

  /**
   * Saves the full hierarchy through the stacks-agents creation layer
   * (countries → states → cities/areas run server-side in one request).
   * @param project_id Numeric project ID
   * @param countries Full countries array to persist
   */
  private async saveShipping(project_id: number, countries: any[]): Promise<void> {
    await this.shippingApi.saveShipping(String(project_id), countries || [])
    if (this.vendor.isMultiVendor) this.vendor.syncSuperVendorSettingsToVendors()
  }

  /**
   * Loads the full shipping hierarchy for the current project.
   * Uses in-memory cache unless refresh is true.
   * @param refresh Force reload from Supabase, bypassing cache
   * @returns Observable emitting the countries array
   */
  getCountries = (refresh: boolean = false): Observable<any> => {
    const project_id = Number(this.builderService.selectedProject)
    if (this.countriesCache && !refresh) {
      return new Observable(observer => { observer.next(this.countriesCache); observer.complete() })
    }
    return new Observable(observer => {
      this.loadShipping(project_id)
        .then(data => { observer.next(data); observer.complete() })
        .catch(err => { console.warn('[ShippingService] load error:', err); observer.next([]); observer.complete() })
    })
  }

  /**
   * Loads the full country → state → city/area hierarchy from the stacks-agents
   * retrieval layer (the 4-table read + assembly now runs server-side) and
   * caches it in memory. Shape is identical to the old direct-Supabase load.
   * @param project_id Numeric project ID
   */
  private async loadShipping(project_id: number): Promise<any[]> {
    const result = await this.shippingApi.getShippingHierarchy(String(project_id))
    this.countriesCache = result
    return result
  }

  /**
   * Returns all world countries from the country-state-city library, sorted alphabetically.
   * @returns Observable emitting array of { countryName, isoCode }
   */
  getAllCountries(): Observable<{ countryName: string, isoCode: string }[]> {
    return new Observable(observer => {
      try {
        const countries = Country.getAllCountries()
        const countryData = countries
          .map(country => ({ countryName: country.name, isoCode: country.isoCode }))
          .sort((a, b) => a.countryName.localeCompare(b.countryName))
        observer.next(countryData)
        observer.complete()
      } catch (error) {
        observer.error(new Error('Failed to fetch countries: ' + error))
      }
    })
  }

  /**
   * Returns all states/governorates for a given country, sorted alphabetically.
   * @param countryIsoCode ISO 3166-1 alpha-2 country code (e.g. 'EG')
   * @returns Observable emitting array of { stateName, isoCode }
   */
    getStates(countryIsoCode: string): Observable<{ stateName: string, isoCode: string }[]> {
      return new Observable(observer => {
        try {
          const states = State.getStatesOfCountry(countryIsoCode)
          const stateData = states ? states.map(state => ({ stateName: state.name, isoCode: state.isoCode })).sort((a, b) => a.stateName.localeCompare(b.stateName))  : []
          observer.next(stateData)
          observer.complete()
        } catch (error) {
          console.log(`Error fetching states for ISO ${countryIsoCode}:`, error)
          observer.next([])
          observer.complete()
        }
      })
    }

  /**
   * Returns city names for a given country + state ISO code pair, sorted alphabetically.
   * @param countryIsoCode ISO 3166-1 alpha-2 country code
   * @param stateIsoCode State/governorate ISO code
   * @returns Observable emitting array of city name strings
   */
    getCities(countryIsoCode: string, stateIsoCode: string): Observable<string[]> {
      return new Observable(observer => {
        try {
          if (!countryIsoCode || !stateIsoCode) {
            observer.next([])
            observer.complete()
            return
          }
          const cities = City.getCitiesOfState(countryIsoCode, stateIsoCode)
          const cityData = cities
            ? cities.map(city => city.name).sort((a, b) => a.localeCompare(b))
            : []
          observer.next(cityData)
          observer.complete()
        } catch (error) {
          console.log(`Error fetching cities for country ISO ${countryIsoCode}, state ISO ${stateIsoCode}:`, error)
          observer.next([])
          observer.complete()
        }
      })
    }
     cityAreas = [
      { cityName:"Alexandria",areas:[
        {"area_name_ar":"ابو قير","area_name_en":"Abu Qir"},
        {"area_name_ar":"الابراهيمية","area_name_en":"Al Ibrahimeyah"},
        {"area_name_ar":"الأزاريطة","area_name_en":"Azarita"},
        {"area_name_ar":"الانفوشى","area_name_en":"Anfoushi"},
        {"area_name_ar":"الدخيلة","area_name_en":"Dekheila"},
        {"area_name_ar":"السيوف","area_name_en":"El Soyof"},
        {"area_name_ar":"العامرية","area_name_en":"Ameria"},
        {"area_name_ar":"اللبان","area_name_en":"El Labban"},
        {"area_name_ar":"المفروزة","area_name_en":"Al Mafrouza"},
        {"area_name_ar":"المنتزه","area_name_en":"El Montaza"},
        {"area_name_ar":"المنشية","area_name_en":"Mansheya"},
        {"area_name_ar":"الناصرية","area_name_en":"Naseria"},
        {"area_name_ar":"امبروزو","area_name_en":"Ambrozo"},
        {"area_name_ar":"باب شرق","area_name_en":"Bab Sharq"},
        {"area_name_ar":"برج العرب","area_name_en":"Borg El Arab"},
        {"area_name_ar":"ستانلى","area_name_en":"Stanley"},
        {"area_name_ar":"سموحة","area_name_en":"Smouha"},
        {"area_name_ar":"سيدى بشر","area_name_en":"Sidi Bishr"},
        {"area_name_ar":"شدس","area_name_en":"Shads"},
        {"area_name_ar":"غيط العنب","area_name_en":"Gheit El Enab"},
        {"area_name_ar":"فلمينج","area_name_en":"Fleming"},
        {"area_name_ar":"فيكتوريا","area_name_en":"Victoria"},
        {"area_name_ar":"كامب شيزار","area_name_en":"Camp Shizar"},
        {"area_name_ar":"كرموز","area_name_en":"Karmooz"},
        {"area_name_ar":"محطة الرمل","area_name_en":"Mahta Alraml"},
        {"area_name_ar":"مينا البصل","area_name_en":"Mina El-Basal"},
        {"area_name_ar":"العصافرة","area_name_en":"Asafra"},
        {"area_name_ar":"العجمي","area_name_en":"Agamy"},
        {"area_name_ar":"بكوس","area_name_en":"Bakos"},
        {"area_name_ar":"بولكلي","area_name_en":"Boulkly"},
        {"area_name_ar":"كليوباترا","area_name_en":"Cleopatra"},
        {"area_name_ar":"جليم","area_name_en":"Glim"},
        {"area_name_ar":"المعمورة","area_name_en":"Al Mamurah"},
        {"area_name_ar":"المندرة","area_name_en":"Al Mandara"},
        {"area_name_ar":"محرم بك","area_name_en":"Moharam Bek"},
        {"area_name_ar":"الشاطبي","area_name_en":"El Shatby"},
        {"area_name_ar":"سيدي جابر","area_name_en":"Sidi Gaber"},
        {"area_name_ar":"الساحل الشمالي","area_name_en":"North Coast"},
        {"area_name_ar":"الحضرة","area_name_en":"Alhadra"},
        {"area_name_ar":"العطارين","area_name_en":"Alattarin"},
        {"area_name_ar":"سيدي كرير","area_name_en":"Sidi Kerir"},
        {"area_name_ar":"الجمرك","area_name_en":"Elgomrok"},
        {"area_name_ar":"المكس","area_name_en":"Al Max"},
        {"area_name_ar":"مارينا","area_name_en":"Marina"},

      ]
    },
    {cityName:"Cairo",areas:[
      {"area_name_ar":"15 مايو","area_name_en":"15 May"},
      {"area_name_ar":"الازبكية","area_name_en":"Al Azbakeyah"},
      {"area_name_ar":"البساتين","area_name_en":"Al Basatin"},
      {"area_name_ar":"التبين","area_name_en":"Tebin"},
      {"area_name_ar":"الخليفة","area_name_en":"El-Khalifa"},
      {"area_name_ar":"الدراسة","area_name_en":"El darrasa"},
      {"area_name_ar":"الدرب الاحمر","area_name_en":"Aldarb Alahmar"},
      {"area_name_ar":"الزاوية الحمراء","area_name_en":"Zawya al-Hamra"},
      {"area_name_ar":"الزيتون","area_name_en":"El-Zaytoun"},
      {"area_name_ar":"الساحل","area_name_en":"Sahel"},
      {"area_name_ar":"السلام","area_name_en":"El Salam"},
      {"area_name_ar":"السيدة زينب","area_name_en":"Sayeda Zeinab"},
      {"area_name_ar":"الشرابية","area_name_en":"El Sharabeya"},
      {"area_name_ar":"مدينة الشروق","area_name_en":"Shorouk"},
      {"area_name_ar":"الظاهر","area_name_en":"El Daher"},
      {"area_name_ar":"العتبة","area_name_en":"Ataba"},
      {"area_name_ar":"القاهرة الجديدة","area_name_en":"New Cairo"},
      {"area_name_ar":"المرج","area_name_en":"El Marg"},
      {"area_name_ar":"عزبة النخل","area_name_en":"Ezbet el Nakhl"},
      {"area_name_ar":"المطرية","area_name_en":"Matareya"},
      {"area_name_ar":"المعادى","area_name_en":"Maadi"},
      {"area_name_ar":"المعصرة","area_name_en":"Maasara"},
      {"area_name_ar":"المقطم","area_name_en":"Mokattam"},
      {"area_name_ar":"المنيل","area_name_en":"Manyal"},
      {"area_name_ar":"الموسكى","area_name_en":"Mosky"},
      {"area_name_ar":"النزهة","area_name_en":"Nozha"},
      {"area_name_ar":"الوايلى","area_name_en":"Waily"},
      {"area_name_ar":"باب الشعرية","area_name_en":"Bab al-Shereia"},
      {"area_name_ar":"بولاق","area_name_en":"Bolaq"},
      {"area_name_ar":"جاردن سيتى","area_name_en":"Garden City"},
      {"area_name_ar":"حدائق القبة","area_name_en":"Hadayek El-Kobba"},
      {"area_name_ar":"حلوان","area_name_en":"Helwan"},
      {"area_name_ar":"دار السلام","area_name_en":"Dar Al Salam"},
      {"area_name_ar":"شبرا","area_name_en":"Shubra"},
      {"area_name_ar":"طره","area_name_en":"Tura"},
      {"area_name_ar":"عابدين","area_name_en":"Abdeen"},
      {"area_name_ar":"عباسية","area_name_en":"Abaseya"},
      {"area_name_ar":"عين شمس","area_name_en":"Ain Shams"},
      {"area_name_ar":"مدينة نصر","area_name_en":"Nasr City"},
      {"area_name_ar":"مصر الجديدة","area_name_en":"New Heliopolis"},
      {"area_name_ar":"مصر القديمة","area_name_en":"Masr Al Qadima"},
      {"area_name_ar":"منشية ناصر","area_name_en":"Mansheya Nasir"},
      {"area_name_ar":"مدينة بدر","area_name_en":"Badr City"},
      {"area_name_ar":"مدينة العبور","area_name_en":"Obour City"},
      {"area_name_ar":"وسط البلد","area_name_en":"Cairo Downtown"},
      {"area_name_ar":"الزمالك","area_name_en":"Zamalek"},
      {"area_name_ar":"قصر النيل","area_name_en":"Kasr El Nile"},
      {"area_name_ar":"الرحاب","area_name_en":"Rehab"},
      {"area_name_ar":"القطامية","area_name_en":"Katameya"},
      {"area_name_ar":"مدينتي","area_name_en":"Madinty"},
      {"area_name_ar":"روض الفرج","area_name_en":"Rod Alfarag"},
      {"area_name_ar":"شيراتون","area_name_en":"Sheraton"},
      {"area_name_ar":"الجمالية","area_name_en":"El-Gamaleya"},
      {"area_name_ar":"العاشر من رمضان","area_name_en":"10th of Ramadan City"},
      {"area_name_ar":"الحلمية","area_name_en":"Helmeyat Alzaytoun"},
      {"area_name_ar":"النزهة الجديدة","area_name_en":"New Nozha"},
      {"area_name_ar":"العاصمة الإدارية","area_name_en":"Capital New"},
    ]},
    {cityName:"Giza",areas:[
      {"area_name_ar":"الجيزة","area_name_en":"Giza"},
      {"area_name_ar":"السادس من أكتوبر","area_name_en":"Sixth of October"},
      {"area_name_ar":"الشيخ زايد","area_name_en":"Cheikh Zayed"},
      {"area_name_ar":"الحوامدية","area_name_en":"Hawamdiyah"},
      {"area_name_ar":"البدرشين","area_name_en":"Al Badrasheen"},
      {"area_name_ar":"الصف","area_name_en":"Saf"},
      {"area_name_ar":"أطفيح","area_name_en":"Atfih"},
      {"area_name_ar":"العياط","area_name_en":"Al Ayat"},
      {"area_name_ar":"الباويطي","area_name_en":"Al-Bawaiti"},
      {"area_name_ar":"منشأة القناطر","area_name_en":"ManshiyetAl Qanater"},
      {"area_name_ar":"أوسيم","area_name_en":"Oaseem"},
      {"area_name_ar":"كرداسة","area_name_en":"Kerdasa"},
      {"area_name_ar":"أبو النمرس","area_name_en":"Abu Nomros"},
      {"area_name_ar":"كفر غطاطي","area_name_en":"Kafr Ghati"},
      {"area_name_ar":"منشأة البكاري","area_name_en":"Manshiyet Al Bakari"},
      {"area_name_ar":"الدقى","area_name_en":"Dokki"},
      {"area_name_ar":"العجوزة","area_name_en":"Agouza"},
      {"area_name_ar":"الهرم","area_name_en":"Haram"},
      {"area_name_ar":"الوراق","area_name_en":"Warraq"},
      {"area_name_ar":"امبابة","area_name_en":"Imbaba"},
      {"area_name_ar":"بولاق الدكرور","area_name_en":"Boulaq Dakrour"},
      {"area_name_ar":"الواحات البحرية","area_name_en":"Al Wahat Al Baharia"},
      {"area_name_ar":"العمرانية","area_name_en":"Omraneya"},
      {"area_name_ar":"المنيب","area_name_en":"Moneeb"},
      {"area_name_ar":"بين السرايات","area_name_en":"Bin Alsarayat"},
      {"area_name_ar":"الكيت كات","area_name_en":"Kit Kat"},
      {"area_name_ar":"المهندسين","area_name_en":"Mohandessin"},
      {"area_name_ar":"فيصل","area_name_en":"Faisal"},
      {"area_name_ar":"أبو رواش","area_name_en":"Abu Rawash"},
      {"area_name_ar":"حدائق الأهرام","area_name_en":"Hadayek Alahram"},
      {"area_name_ar":"الحرانية","area_name_en":"Haraneya"},
      {"area_name_ar":"حدائق اكتوبر","area_name_en":"Hadayek October"},
      {"area_name_ar":"صفط اللبن","area_name_en":"Saft Allaban"},
      {"area_name_ar":"القرية الذكية","area_name_en":"Smart Village"},
      {"area_name_ar":"ارض اللواء","area_name_en":"Ard Ellwaa"},
          ]},
    {cityName:"Beheira",areas:[
      {"area_name_ar":"دمنهور","area_name_en":"Damanhour"},
      {"area_name_ar":"كفر الدوار","area_name_en":"Kafr El Dawar"},
      {"area_name_ar":"رشيد","area_name_en":"Rashid"},
      {"area_name_ar":"إدكو","area_name_en":"Edco"},
      {"area_name_ar":"أبو المطامير","area_name_en":"Abu al-Matamir"},
      {"area_name_ar":"أبو حمص","area_name_en":"Abu Homs"},
      {"area_name_ar":"الدلنجات","area_name_en":"Delengat"},
      {"area_name_ar":"المحمودية","area_name_en":"Mahmoudiyah"},
      {"area_name_ar":"الرحمانية","area_name_en":"Rahmaniyah"},
      {"area_name_ar":"إيتاي البارود","area_name_en":"Itai Baroud"},
      {"area_name_ar":"حوش عيسى","area_name_en":"Housh Eissa"},
      {"area_name_ar":"شبراخيت","area_name_en":"Shubrakhit"},
      {"area_name_ar":"كوم حمادة","area_name_en":"Kom Hamada"},
      {"area_name_ar":"بدر","area_name_en":"Badr"},
      {"area_name_ar":"وادي النطرون","area_name_en":"Wadi Natrun"},
      {"area_name_ar":"النوبارية الجديدة","area_name_en":"New Nubaria"},
      {"area_name_ar":"النوبارية","area_name_en":"Alnoubareya"},
          ]},
    {cityName:"Dakahlia",areas:[
      {"area_name_ar":"المنصورة","area_name_en":"Mansoura"},
      {"area_name_ar":"طلخا","area_name_en":"Talkha"},
      {"area_name_ar":"ميت غمر","area_name_en":"Mitt Ghamr"},
      {"area_name_ar":"دكرنس","area_name_en":"Dekernes"},
      {"area_name_ar":"أجا","area_name_en":"Aga"},
      {"area_name_ar":"منية النصر","area_name_en":"Menia El Nasr"},
      {"area_name_ar":"السنبلاوين","area_name_en":"Sinbillawin"},
      {"area_name_ar":"الكردي","area_name_en":"El Kurdi"},
      {"area_name_ar":"بني عبيد","area_name_en":"Bani Ubaid"},
      {"area_name_ar":"المنزلة","area_name_en":"Al Manzala"},
      {"area_name_ar":"تمي الأمديد","area_name_en":"tami al'amdid"},
      {"area_name_ar":"الجمالية","area_name_en":"aljamalia"},
      {"area_name_ar":"شربين","area_name_en":"Sherbin"},
      {"area_name_ar":"المطرية","area_name_en":"Mataria"},
      {"area_name_ar":"بلقاس","area_name_en":"Belqas"},
      {"area_name_ar":"ميت سلسيل","area_name_en":"Meet Salsil"},
      {"area_name_ar":"جمصة","area_name_en":"Gamasa"},
      {"area_name_ar":"محلة دمنة","area_name_en":"Mahalat Damana"},
      {"area_name_ar":"نبروه","area_name_en":"Nabroh"},
      ]},
      {cityName:"Red Sea",areas:[
        {"area_name_ar":"الغردقة","area_name_en":"Hurghada"},
        {"area_name_ar":"رأس غارب","area_name_en":"Ras Ghareb"},
        {"area_name_ar":"سفاجا","area_name_en":"Safaga"},
        {"area_name_ar":"القصير","area_name_en":"El Qusiar"},
        {"area_name_ar":"مرسى علم","area_name_en":"Marsa Alam"},
        {"area_name_ar":"الشلاتين","area_name_en":"Shalatin"},
        {"area_name_ar":"حلايب","area_name_en":"Halaib"},
        {"area_name_ar":"الدهار","area_name_en":"Aldahar"},
        ]},
        {cityName:"Fayoum",areas:[
        {"area_name_ar":"الفيوم","area_name_en":"Fayoum"},
        {"area_name_ar":"الفيوم الجديدة","area_name_en":"Fayoum El Gedida"},
        {"area_name_ar":"طامية","area_name_en":"Tamiya"},
        {"area_name_ar":"سنورس","area_name_en":"Snores"},
        {"area_name_ar":"إطسا","area_name_en":"Etsa"},
        {"area_name_ar":"إبشواي","area_name_en":"Epschway"},
        {"area_name_ar":"يوسف الصديق","area_name_en":"Yusuf El Sediaq"},
        {"area_name_ar":"الحادقة","area_name_en":"Hadqa"},
        {"area_name_ar":"اطسا","area_name_en":"Atsa"},
        {"area_name_ar":"الجامعة","area_name_en":"Algamaa"},
        {"area_name_ar":"السيالة","area_name_en":"Sayala"},
        ]},
        {cityName:"Gharbiya",areas:[
          {"area_name_ar":"طنطا","area_name_en":"Tanta"},
          {"area_name_ar":"المحلة الكبرى","area_name_en":"Al Mahalla Al Kobra"},
          {"area_name_ar":"كفر الزيات","area_name_en":"Kafr El Zayat"},
          {"area_name_ar":"زفتى","area_name_en":"Zefta"},
          {"area_name_ar":"السنطة","area_name_en":"El Santa"},
          {"area_name_ar":"قطور","area_name_en":"Qutour"},
          {"area_name_ar":"بسيون","area_name_en":"Basion"},
          {"area_name_ar":"سمنود","area_name_en":"Samannoud"},
        ]},
        {cityName:"Ismailia",areas:[
          {"area_name_ar":"الإسماعيلية","area_name_en":"Ismailia"},
          {"area_name_ar":"فايد","area_name_en":"Fayed"},
          {"area_name_ar":"القنطرة شرق","area_name_en":"Qantara Sharq"},
          {"area_name_ar":"القنطرة غرب","area_name_en":"Qantara Gharb"},
          {"area_name_ar":"التل الكبير","area_name_en":"El Tal El Kabier"},
          {"area_name_ar":"أبو صوير","area_name_en":"Abu Sawir"},
          {"area_name_ar":"القصاصين الجديدة","area_name_en":"Kasasien El Gedida"},
          {"area_name_ar":"نفيشة","area_name_en":"Nefesha"},
          {"area_name_ar":"الشيخ زايد","area_name_en":"Sheikh Zayed"},
        ]},
        {cityName:"Menofia",areas:[
          {"area_name_ar":"شبين الكوم","area_name_en":"Shbeen El Koom"},
          {"area_name_ar":"مدينة السادات","area_name_en":"Sadat City"},
          {"area_name_ar":"منوف","area_name_en":"Menouf"},
          {"area_name_ar":"سرس الليان","area_name_en":"Sars El-Layan"},
          {"area_name_ar":"أشمون","area_name_en":"Ashmon"},
          {"area_name_ar":"الباجور","area_name_en":"Al Bagor"},
          {"area_name_ar":"قويسنا","area_name_en":"Quesna"},
          {"area_name_ar":"بركة السبع","area_name_en":"Berkat El Saba"},
          {"area_name_ar":"تلا","area_name_en":"Tala"},
          {"area_name_ar":"الشهداء","area_name_en":"Al Shohada"},
        ]},
        {cityName:"Minya",areas:[
          {"area_name_ar":"المنيا","area_name_en":"Minya"},
          {"area_name_ar":"المنيا الجديدة","area_name_en":"Minya El Gedida"},
          {"area_name_ar":"العدوة","area_name_en":"El Adwa"},
          {"area_name_ar":"مغاغة","area_name_en":"Magagha"},
          {"area_name_ar":"بني مزار","area_name_en":"Bani Mazar"},
          {"area_name_ar":"مطاي","area_name_en":"Mattay"},
          {"area_name_ar":"سمالوط","area_name_en":"Samalut"},
          {"area_name_ar":"المدينة الفكرية","area_name_en":"Madinat El Fekria"},
          {"area_name_ar":"ملوي","area_name_en":"Meloy"},
          {"area_name_ar":"دير مواس","area_name_en":"Deir Mawas"},
          {"area_name_ar":"ابو قرقاص","area_name_en":"Abu Qurqas"},
          {"area_name_ar":"ارض سلطان","area_name_en":"Ard Sultan"},
        ]},
        {cityName:"Qaliubiya",areas:[
          {"area_name_ar":"بنها","area_name_en":"Banha"},
          {"area_name_ar":"قليوب","area_name_en":"Qalyub"},
          {"area_name_ar":"شبرا الخيمة","area_name_en":"Shubra Al Khaimah"},
          {"area_name_ar":"القناطر الخيرية","area_name_en":"Al Qanater Charity"},
          {"area_name_ar":"الخانكة","area_name_en":"Khanka"},
          {"area_name_ar":"كفر شكر","area_name_en":"Kafr Shukr"},
          {"area_name_ar":"طوخ","area_name_en":"Tukh"},
          {"area_name_ar":"قها","area_name_en":"Qaha"},
          {"area_name_ar":"العبور","area_name_en":"Obour"},
          {"area_name_ar":"الخصوص","area_name_en":"Khosous"},
          {"area_name_ar":"شبين القناطر","area_name_en":"Shibin Al Qanater"},
          {"area_name_ar":"مسطرد","area_name_en":"Mostorod"},
        ]},
        {cityName:"New Valley",areas:[
          {"area_name_ar":"الخارجة","area_name_en":"El Kharga"},
          {"area_name_ar":"باريس","area_name_en":"Paris"},
          {"area_name_ar":"موط","area_name_en":"Mout"},
          {"area_name_ar":"الفرافرة","area_name_en":"Farafra"},
          {"area_name_ar":"بلاط","area_name_en":"Balat"},
          {"area_name_ar":"الداخلة","area_name_en":"Dakhla"},
        ]},
        {cityName:"Suez",areas:[
          {"area_name_ar":"السويس","area_name_en":"Suez"},
          {"area_name_ar":"الجناين","area_name_en":"Alganayen"},
          {"area_name_ar":"عتاقة","area_name_en":"Ataqah"},
          {"area_name_ar":"العين السخنة","area_name_en":"Ain Sokhna"},
          {"area_name_ar":"فيصل","area_name_en":"Faysal"},
        ]},
        {cityName:"Aswan",areas:[
          {"area_name_ar":"أسوان","area_name_en":"Aswan"},
          {"area_name_ar":"أسوان الجديدة","area_name_en":"Aswan El Gedida"},
          {"area_name_ar":"دراو","area_name_en":"Drau"},
          {"area_name_ar":"كوم أمبو","area_name_en":"Kom Ombo"},
          {"area_name_ar":"نصر النوبة","area_name_en":"Nasr Al Nuba"},
          {"area_name_ar":"كلابشة","area_name_en":"Kalabsha"},
          {"area_name_ar":"إدفو","area_name_en":"Edfu"},
          {"area_name_ar":"الرديسية","area_name_en":"Al-Radisiyah"},
          {"area_name_ar":"البصيلية","area_name_en":"Al Basilia"},
          {"area_name_ar":"السباعية","area_name_en":"Al Sibaeia"},
          {"area_name_ar":"ابوسمبل السياحية","area_name_en":"Abo Simbl Al Siyahia"},
          {"area_name_ar":"مرسى علم","area_name_en":"Marsa Alam"},
        ]},
        {cityName:"Assiut",areas:[
          {"area_name_ar":"أسيوط","area_name_en":"Assiut"},
          {"area_name_ar":"أسيوط الجديدة","area_name_en":"Assiut El Gedida"},
          {"area_name_ar":"ديروط","area_name_en":"Dayrout"},
          {"area_name_ar":"منفلوط","area_name_en":"Manfalut"},
          {"area_name_ar":"القوصية","area_name_en":"Qusiya"},
          {"area_name_ar":"أبنوب","area_name_en":"Abnoub"},
          {"area_name_ar":"أبو تيج","area_name_en":"Abu Tig"},
          {"area_name_ar":"الغنايم","area_name_en":"El Ghanaim"},
          {"area_name_ar":"ساحل سليم","area_name_en":"Sahel Selim"},
          {"area_name_ar":"البداري","area_name_en":"El Badari"},
          {"area_name_ar":"صدفا","area_name_en":"Sidfa"},
        ]},
        {cityName:"Beni Suef",areas:[
          {"area_name_ar":"بني سويف","area_name_en":"Bani Sweif"},
          {"area_name_ar":"بني سويف الجديدة","area_name_en":"Beni Suef El Gedida"},
          {"area_name_ar":"الواسطى","area_name_en":"Al Wasta"},
          {"area_name_ar":"ناصر","area_name_en":"Naser"},
          {"area_name_ar":"إهناسيا","area_name_en":"Ehnasia"},
          {"area_name_ar":"ببا","area_name_en":"beba"},
          {"area_name_ar":"الفشن","area_name_en":"Fashn"},
          {"area_name_ar":"سمسطا","area_name_en":"Somasta"},
          {"area_name_ar":"الاباصيرى","area_name_en":"Alabbaseri"},
          {"area_name_ar":"مقبل","area_name_en":"Mokbel"},
        ]},
        {cityName:"Port Said",areas:[
          {"area_name_ar":"بورفؤاد","area_name_en":"Port Fouad"},
          {"area_name_ar":"بورسعيد","area_name_en":"PorSaid"},
          {"area_name_ar":"العرب","area_name_en":"Alarab"},
          {"area_name_ar":"حى الزهور","area_name_en":"Zohour"},
          {"area_name_ar":"حى الشرق","area_name_en":"Alsharq"},
          {"area_name_ar":"حى الضواحى","area_name_en":"Aldawahi"},
          {"area_name_ar":"حى المناخ","area_name_en":"Almanakh"},
          {"area_name_ar":"حى مبارك","area_name_en":"Mubarak"},
        ]},
       {cityName:"Damietta",areas:[
        {"area_name_ar":"دمياط","area_name_en":"Damietta"},
        {"area_name_ar":"دمياط الجديدة","area_name_en":"New Damietta"},
        {"area_name_ar":"رأس البر","area_name_en":"Ras El Bar"},
        {"area_name_ar":"فارسكور","area_name_en":"Faraskour"},
        {"area_name_ar":"الزرقا","area_name_en":"Zarqa"},
        {"area_name_ar":"السرو","area_name_en":"alsaru"},
        {"area_name_ar":"الروضة","area_name_en":"alruwda"},
        {"area_name_ar":"كفر البطيخ","area_name_en":"Kafr El-Batikh"},
        {"area_name_ar":"عزبة البرج","area_name_en":"Azbet Al Burg"},
        {"area_name_ar":"ميت أبو غالب","area_name_en":"Meet Abou Ghalib"},
        {"area_name_ar":"كفر سعد","area_name_en":"Kafr Saad"},
        ]},
        {cityName:"Sharkia",areas:[
          {"area_name_ar":"الزقازيق","area_name_en":"Zagazig"},
          {"area_name_ar":"العاشر من رمضان","area_name_en":"Al Ashr Men Ramadan"},
          {"area_name_ar":"منيا القمح","area_name_en":"Minya Al Qamh"},
          {"area_name_ar":"بلبيس","area_name_en":"Belbeis"},
          {"area_name_ar":"مشتول السوق","area_name_en":"Mashtoul El Souq"},
          {"area_name_ar":"القنايات","area_name_en":"Qenaiat"},
          {"area_name_ar":"أبو حماد","area_name_en":"Abu Hammad"},
          {"area_name_ar":"القرين","area_name_en":"El Qurain"},
          {"area_name_ar":"ههيا","area_name_en":"Hehia"},
          {"area_name_ar":"أبو كبير","area_name_en":"Abu Kabir"},
          {"area_name_ar":"فاقوس","area_name_en":"Faccus"},
          {"area_name_ar":"الصالحية الجديدة","area_name_en":"El Salihia El Gedida"},
          {"area_name_ar":"الإبراهيمية","area_name_en":"Al Ibrahimiyah"},
          {"area_name_ar":"ديرب نجم","area_name_en":"Deirb Negm"},
          {"area_name_ar":"كفر صقر","area_name_en":"Kafr Saqr"},
          {"area_name_ar":"أولاد صقر","area_name_en":"Awlad Saqr"},
          {"area_name_ar":"الحسينية","area_name_en":"Husseiniya"},
          {"area_name_ar":"صان الحجر القبلية","area_name_en":"san alhajar alqablia"},
          {"area_name_ar":"منشأة أبو عمر","area_name_en":"Manshayat Abu Omar"},
        ]},
        {cityName:"South Sinai",areas:[
          {"area_name_ar":"الطور","area_name_en":"Al Toor"},
          {"area_name_ar":"شرم الشيخ","area_name_en":"Sharm El-Shaikh"},
          {"area_name_ar":"دهب","area_name_en":"Dahab"},
          {"area_name_ar":"نويبع","area_name_en":"Nuweiba"},
          {"area_name_ar":"طابا","area_name_en":"Taba"},
          {"area_name_ar":"سانت كاترين","area_name_en":"Saint Catherine"},
          {"area_name_ar":"أبو رديس","area_name_en":"Abu Redis"},
          {"area_name_ar":"أبو زنيمة","area_name_en":"Abu Zenaima"},
          {"area_name_ar":"رأس سدر","area_name_en":"Ras Sidr"},
        ]},

        {cityName:"Kafr Al sheikh",areas:[
          {"area_name_ar":"وسط البلد كفر الشيخ","area_name_en":"Kafr El Sheikh Downtown"},
          {"area_name_ar":"كفر الشيخ","area_name_en":"Kafr El Sheikh"},
          {"area_name_ar":"دسوق","area_name_en":"Desouq"},
          {"area_name_ar":"فوه","area_name_en":"Fooh"},
          {"area_name_ar":"مطوبس","area_name_en":"Metobas"},
          {"area_name_ar":"برج البرلس","area_name_en":"Burg Al Burullus"},
          {"area_name_ar":"بلطيم","area_name_en":"Baltim"},
          {"area_name_ar":"مصيف بلطيم","area_name_en":"Masief Baltim"},
          {"area_name_ar":"الحامول","area_name_en":"Hamol"},
          {"area_name_ar":"بيلا","area_name_en":"Bella"},
          {"area_name_ar":"الرياض","area_name_en":"Riyadh"},
          {"area_name_ar":"سيدي سالم","area_name_en":"Sidi Salm"},
          {"area_name_ar":"قلين","area_name_en":"Qellen"},
          {"area_name_ar":"سيدي غازي","area_name_en":"Sidi Ghazi"},
        ]},
        {cityName:"Matrouh",areas:[
          {"area_name_ar":"مرسى مطروح","area_name_en":"Marsa Matrouh"},
          {"area_name_ar":"الحمام","area_name_en":"El Hamam"},
          {"area_name_ar":"العلمين","area_name_en":"Alamein"},
          {"area_name_ar":"الضبعة","area_name_en":"Dabaa"},
          {"area_name_ar":"النجيلة","area_name_en":"Al-Nagila"},
          {"area_name_ar":"سيدي براني","area_name_en":"Sidi Brani"},
          {"area_name_ar":"السلوم","area_name_en":"Salloum"},
          {"area_name_ar":"سيوة","area_name_en":"Siwa"},
          {"area_name_ar":"مارينا","area_name_en":"Marina"},
          {"area_name_ar":"الساحل الشمالى","area_name_en":"North Coast"},
        ]},
        {cityName:"Luxor",areas:[
          {"area_name_ar":"الأقصر","area_name_en":"Luxor"},
          {"area_name_ar":"الأقصر الجديدة","area_name_en":"New Luxor"},
          {"area_name_ar":"إسنا","area_name_en":"Esna"},
          {"area_name_ar":"طيبة الجديدة","area_name_en":"New Tiba"},
          {"area_name_ar":"الزينية","area_name_en":"Al ziynia"},
          {"area_name_ar":"البياضية","area_name_en":"Al Bayadieh"},
          {"area_name_ar":"القرنة","area_name_en":"Al Qarna"},
          {"area_name_ar":"أرمنت","area_name_en":"Armant"},
          {"area_name_ar":"الطود","area_name_en":"Al Tud"},
        ]},
        {cityName:"Qena",areas:[
          {"area_name_ar":"قنا","area_name_en":"Qena"},
          {"area_name_ar":"قنا الجديدة","area_name_en":"New Qena"},
          {"area_name_ar":"ابو طشت","area_name_en":"Abu Tesht"},
          {"area_name_ar":"نجع حمادي","area_name_en":"Nag Hammadi"},
          {"area_name_ar":"دشنا","area_name_en":"Deshna"},
          {"area_name_ar":"الوقف","area_name_en":"Alwaqf"},
          {"area_name_ar":"قفط","area_name_en":"Qaft"},
          {"area_name_ar":"نقادة","area_name_en":"Naqada"},
          {"area_name_ar":"فرشوط","area_name_en":"Farshout"},
          {"area_name_ar":"قوص","area_name_en":"Quos"},
        ]},
        {cityName:"North Sinai",areas:[
          {"area_name_ar":"العريش","area_name_en":"Arish"},
          {"area_name_ar":"الشيخ زويد","area_name_en":"Sheikh Zowaid"},
          {"area_name_ar":"نخل","area_name_en":"Nakhl"},
          {"area_name_ar":"رفح","area_name_en":"Rafah"},
          {"area_name_ar":"بئر العبد","area_name_en":"Bir al-Abed"},
          {"area_name_ar":"الحسنة","area_name_en":"Al Hasana"},
        ]},
        {cityName:"Sohag",areas:[
          {"area_name_ar":"سوهاج","area_name_en":"Sohag"},
          {"area_name_ar":"سوهاج الجديدة","area_name_en":"Sohag El Gedida"},
          {"area_name_ar":"أخميم","area_name_en":"Akhmeem"},
          {"area_name_ar":"أخميم الجديدة","area_name_en":"Akhmim El Gedida"},
          {"area_name_ar":"البلينا","area_name_en":"Albalina"},
          {"area_name_ar":"المراغة","area_name_en":"El Maragha"},
          {"area_name_ar":"المنشأة","area_name_en":"almunsha'a"},
          {"area_name_ar":"دار السلام","area_name_en":"Dar AISalaam"},
          {"area_name_ar":"جرجا","area_name_en":"Gerga"},
          {"area_name_ar":"جهينة الغربية","area_name_en":"Jahina Al Gharbia"},
          {"area_name_ar":"ساقلته","area_name_en":"Saqilatuh"},
          {"area_name_ar":"طما","area_name_en":"Tama"},
          {"area_name_ar":"طهطا","area_name_en":"Tahta"},
          {"area_name_ar":"الكوثر","area_name_en":"Alkawthar"}
        ]},
        {cityName:"Jeddah",areas:[
          {"area_name_ar":"حي الروضة","area_name_en":"Al Rawdah"},
          {"area_name_ar":"حي الفيصلية","area_name_en":"Al Faisaliyyah"},
          {"area_name_ar":"حي السلامة","area_name_en":"Al Salamah"},
          {"area_name_ar":"حي الزهرة","area_name_en":"Al Zahra"},
          {"area_name_ar":"حي البوادي","area_name_en":"Al Bawadi"},
          {"area_name_ar":"حي الخالدية","area_name_en":"Al Khalidiyyah"},
          {"area_name_ar":"حي العزيزية","area_name_en":"Al Aziziyyah"},
          {"area_name_ar":"حي الشرفية","area_name_en":"Al Sharafiyyah"},
          {"area_name_ar":"حي النهضة","area_name_en":"Al Nahdhah"},
          {"area_name_ar":"حي النعيم","area_name_en":"Al Naim"},
          {"area_name_ar":"حي المحمدية","area_name_en":"Al Muhammadiyyah"},
          {"area_name_ar":"حي البساتين","area_name_en":"Al Basatin"},
          {"area_name_ar":"حي الرحاب","area_name_en":"Al Rahab"},
          {"area_name_ar":"حي الربوة","area_name_en":"Al Rabwah"},
          {"area_name_ar":"حي النزهة","area_name_en":"Al Nuzhah"},
          {"area_name_ar":"حي المروة","area_name_en":"Al Marwah"},
          {"area_name_ar":"حي الشاطئ","area_name_en":"Al Shati"},
          {"area_name_ar":"حي الصفا","area_name_en":"Al Safa"},
          {"area_name_ar":"حي مشرفة","area_name_en":"Mishrifah"},
          {"area_name_ar":"حي النسيم","area_name_en":"Al Naseem"},
          {"area_name_ar":"حي السليمانية","area_name_en":"Al Sulaymaniyyah"},
          {"area_name_ar":"حي الفيحاء","area_name_en":"Al Fayha"},
          {"area_name_ar":"حي الورود","area_name_en":"Al Wurud"}
        ]}


    ]

  /**
   * Returns area names for a city from the hardcoded Fincart cityAreas list, sorted alphabetically.
   * @param cityName City name to look up
   * @returns Observable emitting array of English area name strings
   */
  getAreas(cityName: string): Observable<string[]> {
      return new Observable(observer => {
        try {
          const city = this.cityAreas.find(c => c.cityName === cityName)
          const areaData = city
            ? city.areas.map(area => area.area_name_en).sort((a, b) => a.localeCompare(b))
            : []
          observer.next(areaData)
          observer.complete()
        } catch (error) {
          console.log(`Error fetching areas for city ${cityName}:`, error)
          observer.next([])
          observer.complete()
        }
      })
    }

  /**
   * Returns all city names from the hardcoded Fincart cityAreas list.
   * @returns Array of city name strings
   */
  getFincartCities(): string[] {
      return this.cityAreas.map(c => c.cityName);
    }

  /**
   * Returns the full cityAreas array including each city's area list.
   * @returns Array of { cityName, areas[] } objects
   */
  getFincartCityAreas(): any[] {
      return this.cityAreas;
    }

  /**
   * Fuzzy-matches an address city string against the Fincart city list.
   * @param addressCity Raw city string from an order address
   * @returns Canonical Fincart city name, or null if no match found
   */
  findFincartCity(addressCity: string): string | null {
      if (!addressCity) return null;
      const cityLower = addressCity.toLowerCase();
      const match = this.cityAreas.find(c =>
        c.cityName.toLowerCase() === cityLower ||
        c.cityName.toLowerCase().includes(cityLower) ||
        cityLower.includes(c.cityName.toLowerCase())
      );
      return match?.cityName || null;
    }

  /**
   * Fuzzy-matches an address area string (Arabic or English) within a Fincart city.
   * @param cityName Canonical Fincart city name
   * @param addressArea Raw area string from an order address
   * @returns English area name, or null if no match found
   */
  findFincartArea(cityName: string, addressArea: string): string | null {
      if (!cityName || !addressArea) return null
      const city = this.cityAreas.find(c => c.cityName === cityName);
      if (!city) return null;

      const areaLower = addressArea.toLowerCase();
      const match = city.areas.find((a: any) =>
        a.area_name_en.toLowerCase() === areaLower ||
        a.area_name_ar === addressArea ||
        a.area_name_en.toLowerCase().includes(areaLower) ||
        areaLower.includes(a.area_name_en.toLowerCase())
      );
      return match?.area_name_en || null;
    }
}
